/**
 * @module app.RasterSynchronizer
 */
import {WINTER_LAYER_PLACEHOLDER} from './basemaps.js';
import olSourceWMTS from 'ol/source/WMTS.js';
import olLayerTile from 'ol/layer/Tile.js';
import olcsRasterSynchronizer from 'ol-cesium/src/olcs/RasterSynchronizer.ts';
import {convertUrlToCesium, sourceToImageryProvider} from 'ol-cesium/src/olcs/core.ts';
import VectorTileLayer from 'ol/layer/VectorTile';

const isLocalhost = document.location.hostname === 'localhost';
const swisstopoPrefix = isLocalhost ?
  'https://wmts.geo.admin.ch' :
  'https://wmts10{s}.geo.admin.ch';

/**
 * @const
 * @type {Object.<string, string>}
 */
export const LAYERNAME_TO_SWISSTOPO_URL = {
  'ch.swisstopo.pixelkarte-farbe': swisstopoPrefix + '/1.0.0/ch.swisstopo.swissimage-product/default/current/4326/{z}/{x}/{y}.jpeg',
  [WINTER_LAYER_PLACEHOLDER]: swisstopoPrefix + '/1.0.0/ch.swisstopo.swisstlm3d-karte-farbe.3d/default/current/4326/{z}/{x}/{y}.jpeg',
  'ch.swisstopo.pixelkarte-grau': swisstopoPrefix + '/1.0.0/ch.swisstopo.swisstlm3d-karte-grau.3d/default/current/4326/{z}/{x}/{y}.jpeg',
  'ch.swisstopo.swissimage': swisstopoPrefix + '/1.0.0/ch.swisstopo.swissimage-product/default/current/4326/{z}/{x}/{y}.jpeg',

  'ch.swisstopo.hangneigung-ueber_30': swisstopoPrefix + '/1.0.0/ch.swisstopo.hangneigung-ueber_30/default/current/4326/{z}/{x}/{y}.png',
  'ch.bafu.wrz-wildruhezonen_portal': swisstopoPrefix + '/1.0.0/ch.bafu.wrz-wildruhezonen_portal/default/current/4326/{z}/{x}/{y}.png',
  'ch.bafu.wrz-jagdbanngebiete_select': swisstopoPrefix + '/1.0.0/ch.bafu.wrz-jagdbanngebiete_select/default/current/4326/{z}/{x}/{y}.png',
  'ch.bafu.alpweiden-herdenschutzhunde': swisstopoPrefix + '/1.0.0/ch.bafu.alpweiden-herdenschutzhunde/default/current/4326/{z}/{x}/{y}.png',

  'ch.swisstopo.swisstlm3d-wanderwege': swisstopoPrefix + '/1.0.0/ch.swisstopo.swisstlm3d-wanderwege/default/current/4326/{z}/{x}/{y}.png',
};

/**
 * @const {Array<number>}
 * Source: https://wmts.geo.admin.ch/EPSG/4326/1.0.0/WMTSCapabilities.xml
 */
export const WMTS_4326_BOUNDS = [5.140242, 45.398181, 11.47757, 48.230651];

/**
 * @param {(Cesium.UrlTemplateImageryProvider|Cesium.CesiumTerrainProvider)} tileProvider
 */
export function addErrorWrapper(tileProvider) {
  const handler = tileProvider.errorEvent;
  handler.addEventListener((event) => {
    // Handle messages such as "Failed to obtain terrain tile X: 4263 Y: 989 Level: 12."
    // By adding this event listener, the Cesium default handler that calls console.log
    // is disabled. By using console.debug instead, the messages are only shown in when
    // verbose output is enabled.
    console.debug(event['message']);
  });
}


class RasterSynchronizer extends olcsRasterSynchronizer {
  constructor(map, scene) {
    super(map, scene);
    /**
     * @type {Cesium.Rectangle}
     * @private
     */
    this.wmtsRectangle_ = Cesium.Rectangle.fromDegrees.apply(null, WMTS_4326_BOUNDS);
  }

  /**
   * @inheritDoc
   */
  convertLayerToCesiumImageries(olLayer, viewProj) {
    const imageryLayerOptions = {
      minimumTerrainLevel: 7
    };

    // try to convert a swisstopo layer
    if (olLayer instanceof olLayerTile) {
      const source = olLayer.getSource();
      if (source instanceof olSourceWMTS) {
        const url = LAYERNAME_TO_SWISSTOPO_URL[source.getLayer()];
        if (url) {
          const imageryProvider = new Cesium.UrlTemplateImageryProvider({
            url: url,
            rectangle: this.wmtsRectangle_,
            tilingScheme: new Cesium.GeographicTilingScheme(),
            minimumLevel: 7,
            maximumLevel: 17,
            tileWidth: 256,
            tileHeight: 256,
            hasAlphaChannel: true,
            subdomains: '01234',
            credit: 'swissimage 3D'
          });

          addErrorWrapper(imageryProvider);

          return [new Cesium.ImageryLayer(imageryProvider, imageryLayerOptions)];
        }
      }
    }

    // Convert Vector tile layer
    if (olLayer instanceof VectorTileLayer) {
      const source = olLayer.getSource();
      const imageryProvider = sourceToImageryProvider(this.map, source, this.map.getView(), olLayer);
      addErrorWrapper(imageryProvider);
      return [new Cesium.ImageryLayer(imageryProvider, imageryLayerOptions)];
    }

    // try to convert a schweizmobil layer
    let result;
    const urls = /** @type {string} */ (olLayer.get('urls'));
    const layername = /** @type {string} */ (olLayer.get('layername'));
    const time = /** @type {string} */ (olLayer.get('time'));
    const ignore = /** @type {boolean} */ (olLayer.get('cesiumIgnore'));
    if (!ignore && urls && layername && time) {
      const urlDefinition = convertUrlToCesium(urls);
      const wmtsUrl = urlDefinition.url + '1.0.0/' + layername + '/default/' + time + '/wgs84/{z}/{y}/{x}.png';
      const urlTemplateProvider = new Cesium.UrlTemplateImageryProvider({
        url: wmtsUrl,
        subdomains: urlDefinition.subdomains,
        rectangle: this.wmtsRectangle_,
        tilingScheme: new Cesium.GeographicTilingScheme(),
        minimumLevel: 7,
        maximumLevel: 17,
        tileWidth: 256,
        tileHeight: 256,
        credit: 'Schweizmobil'
      });

      addErrorWrapper(urlTemplateProvider);

      // FIXME: temporary hack to workaround the level shift (level should be level + 1)
      urlTemplateProvider.requestImage = function(x, y, level) {
        return Cesium.UrlTemplateImageryProvider.prototype.requestImage.call(this, x, y, level + 1);
      };
      result = new Cesium.ImageryLayer(urlTemplateProvider, imageryLayerOptions);
    }
    return result ? [result] : null;
  }
}

export default RasterSynchronizer;
